import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { getErrorMessage } from '../services/errorMessages';
import SignatureCanvas from 'react-signature-canvas';
import { FiPlay, FiPause, FiSquare, FiCoffee, FiFileText, FiClock, FiPlus, FiX, FiEye, FiPrinter, FiCalendar, FiTrash2 } from 'react-icons/fi';
import ConfirmModal from '../components/ConfirmModal';
import Calendar from '../components/Calendar';
import TimePicker from '../components/TimePicker';
import Dropdown from '../components/Dropdown';
import './UserDashboard.css';

const ACT_SEP = ' || ';
const parseActivities = (val, optionNames) => {
  if (!val) return [];
  // New separator
  if (val.includes(' || ')) return val.split(' || ').filter(Boolean);
  // Legacy comma separator - match against known activity names
  if (optionNames && optionNames.length > 0) {
    const result = [];
    let remaining = val;
    const sorted = [...optionNames].sort((a, b) => b.length - a.length);
    while (remaining.length > 0) {
      let matched = false;
      for (const name of sorted) {
        if (remaining === name || remaining.startsWith(name + ', ')) {
          result.push(name);
          remaining = remaining.slice(name.length);
          if (remaining.startsWith(', ')) remaining = remaining.slice(2);
          matched = true;
          break;
        }
      }
      if (!matched) {
        result.push(remaining);
        break;
      }
    }
    return result.filter(Boolean);
  }
  return [val];
};

const ActivityMultiSelect = ({ options, value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({});
  const btnRef = useRef(null);
  const dropRef = useRef(null);
  const optionNames = options.map(o => o.name);
  const selected = parseActivities(value, optionNames);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (btnRef.current?.contains(e.target)) return;
      if (dropRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const calcPos = useCallback(() => {
    if (!btnRef.current) return;
    const rect = btnRef.current.getBoundingClientRect();
    if (rect.bottom < 0 || rect.top > window.innerHeight) { setOpen(false); return; }
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const menuH = Math.min(options.length * 44 + 8, 260);
    const openUp = spaceBelow < menuH && spaceAbove > spaceBelow;
    const w = Math.max(rect.width, 280);
    const left = Math.max(8, Math.min(rect.left, window.innerWidth - w - 8));
    setPos({
      left, width: w,
      maxHeight: Math.max(Math.min(menuH, openUp ? spaceAbove - 8 : spaceBelow - 8), 100),
      ...(openUp ? { bottom: window.innerHeight - rect.top + 4 } : { top: rect.bottom + 4 }),
    });
  }, [options.length]);

  useEffect(() => {
    if (!open) return;
    calcPos();
    const onScroll = () => calcPos();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => { window.removeEventListener('scroll', onScroll, true); window.removeEventListener('resize', onScroll); };
  }, [open, calcPos]);

  const toggle = (name) => {
    const cur = new Set(selected);
    if (cur.has(name)) cur.delete(name); else cur.add(name);
    onChange([...cur].join(ACT_SEP));
  };

  return (
    <div className="act-multi">
      <div ref={btnRef} className={`act-multi-btn ${open ? 'open' : ''}`} onClick={() => { if (!open) calcPos(); setOpen(!open); }}>
        {selected.length > 0 ? (
          <span className="act-multi-count">{selected.length} არჩეული</span>
        ) : (
          <span className="act-multi-placeholder">აირჩიეთ</span>
        )}
        <span className="act-multi-arrow">{open ? '▲' : '▼'}</span>
      </div>
      {open && createPortal(
        <div ref={dropRef} className="act-multi-drop" style={{ position: 'fixed', zIndex: 99999, overflowY: 'auto', ...pos }}>
          {options.map((a) => (
            <div key={a.id} className="act-multi-item" onClick={() => toggle(a.name)}>
              <input type="checkbox" checked={selected.includes(a.name)} readOnly />
              <span>{a.name}</span>
            </div>
          ))}
          {options.length === 0 && <div style={{padding: '10px 14px', color: '#94a3b8', fontSize: 13}}>აქტივობები არ არის</div>}
        </div>,
        document.body
      )}
    </div>
  );
};

const UserDashboard = () => {
  const { user } = useAuth();
  const [session, setSession] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [breakElapsed, setBreakElapsed] = useState(0);
  const timerRef = useRef(null);

  // Server time
  const [serverTime, setServerTime] = useState(null);

  // Templates, clients, activities
  const [templates, setTemplates] = useState([]);
  const [clients, setClients] = useState([]);
  const [activities, setActivities] = useState([]);
  const [filledList, setFilledList] = useState([]);

  // Fill form
  const [showFill, setShowFill] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [headerValues, setHeaderValues] = useState({});
  const [rowData, setRowData] = useState([]);
  const [fullRowData, setFullRowData] = useState({});
  const [fillNote, setFillNote] = useState('');
  const [fillStartTime, setFillStartTime] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);

  // Signature
  const [showSignModal, setShowSignModal] = useState(null);
  const sigRef = useRef(null);

  // View
  const [showView, setShowView] = useState(null);
  const [viewData, setViewData] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);

  // Calendar fill
  const [calendarWeekStart, setCalendarWeekStart] = useState(null);
  const [calendarDays, setCalendarDays] = useState({});

  // Client detail
  const [showClientDetail, setShowClientDetail] = useState(null);
  const [clientAttachments, setClientAttachments] = useState([]);
  const [clientDateFilter, setClientDateFilter] = useState('');
  const [clientDetailLoading, setClientDetailLoading] = useState(false);

  // My work history
  const todayStr2 = new Date().toISOString().split('T')[0];
  const [myHistory, setMyHistory] = useState([]);
  const [historyPeriod, setHistoryPeriod] = useState('month');
  const [historyDateFrom, setHistoryDateFrom] = useState('');
  const [historyDateTo, setHistoryDateTo] = useState(todayStr2);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [filledPage, setFilledPage] = useState(1);

  const fetchMyHistory = useCallback(async () => {
    try {
      const now = new Date();
      let from, to;
      if (historyPeriod === 'today') {
        from = to = now.toISOString().split('T')[0];
      } else if (historyPeriod === 'week') {
        const day = now.getDay() || 7;
        const mon = new Date(now); mon.setDate(now.getDate() - day + 1);
        from = mon.toISOString().split('T')[0];
        to = now.toISOString().split('T')[0];
      } else if (historyPeriod === 'month') {
        const start = new Date(now.getFullYear(), now.getMonth(), 1);
        from = start.toISOString().split('T')[0];
        to = now.toISOString().split('T')[0];
      } else if (historyPeriod === 'custom' && historyDateFrom && historyDateTo) {
        from = historyDateFrom;
        to = historyDateTo;
      }
      let url = '/work/my-history';
      if (from && to) url += `?from=${from}&to=${to}`;
      const { data } = await api.get(url);
      setMyHistory(data);
    } catch { /* ignored */ }
  }, [historyPeriod, historyDateFrom, historyDateTo]);

  useEffect(() => { fetchMyHistory(); }, [fetchMyHistory]);

  const fetchData = useCallback(async () => {
    try {
      const [sessRes, tmplRes, clRes, actRes, filledRes, timeRes] = await Promise.all([
        api.get('/work/active'),
        api.get('/templates'),
        api.get('/clients'),
        api.get('/activities'),
        api.get('/filled-attachments/my'),
        api.get('/work/server-time'),
      ]);
      setSession(sessRes.data);
      setTemplates(tmplRes.data);
      setClients(clRes.data);
      setActivities(actRes.data);
      setFilledList(filledRes.data);
      setServerTime(new Date(timeRes.data.now));
    } catch { /* ignored */ }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Timer
  useEffect(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (!session || session.status === 'completed') return;

    timerRef.current = setInterval(() => {
      const start = new Date(session.started_at).getTime();
      const now = Date.now();
      let totalBreak = 0;
      (session.breaks || []).forEach((b) => {
        if (b.ended_at) {
          totalBreak += new Date(b.ended_at).getTime() - new Date(b.started_at).getTime();
        } else {
          totalBreak += now - new Date(b.started_at).getTime();
        }
      });
      setElapsed(Math.floor((now - start) / 1000));
      setBreakElapsed(Math.floor(totalBreak / 1000));
    }, 1000);

    return () => clearInterval(timerRef.current);
  }, [session]);

  const formatTime = (s) => {
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
  };

  const startWork = () => {
    setConfirmAction({
      title: 'სამუშაოს დაწყება',
      message: 'ნამდვილად გსურთ სამუშაოს დაწყება?',
      type: 'warning',
      confirmText: 'დაწყება',
      onConfirm: async () => {
        try {
          const { data } = await api.post('/work/start');
          setSession({ ...data, breaks: [] });
        } catch { /* ignored */ }
        setConfirmAction(null);
      },
    });
  };

  const endWork = () => {
    // Check unfilled templates (exclude auto_open_after_work - they open after work ends)
    const currentSessionId = session?.id;
    const filledInSession = filledList.filter((fa) => fa.session_id === currentSessionId);
    const filledSessionIds = new Set(filledInSession.map((fa) => fa.template_id));
    const sNow = serverTime || new Date();
    const wkStart = new Date(sNow); wkStart.setDate(wkStart.getDate() - ((wkStart.getDay() + 6) % 7)); wkStart.setHours(0, 0, 0, 0);
    const filledThisWeekIds = new Set(filledList.filter((fa) => new Date(fa.completed_at || fa.created_at) >= wkStart).map((fa) => fa.template_id));
    const isFilledForTemplate = (t) => t.fill_frequency === 'weekly' ? filledThisWeekIds.has(t.id) : filledSessionIds.has(t.id);
    const unfilled = todayTemplates.filter((t) => !t.auto_open_after_work && !isFilledForTemplate(t));

    // Block if any template is unfilled
    if (unfilled.length > 0) {
      setConfirmAction({
        title: 'დანართები შეუვსებელია',
        message: `ყველა დანართი უნდა იყოს შევსებული:\n${unfilled.map((t) => `• ${t.title}`).join('\n')}`,
        type: 'delete',
        confirmText: 'გასაგებია',
        onConfirm: () => setConfirmAction(null),
      });
      return;
    }

    setConfirmAction({
      title: 'სამუშაოს დასრულება',
      message: 'ყველა დანართი შევსებულია. ნამდვილად გსურთ სამუშაოს დასრულება?',
      type: 'warning',
      confirmText: 'დასრულება',
      onConfirm: async () => {
        try {
          await api.put('/work/end');
          // Check for after-work templates before clearing session
          const afterWorkTemplates = todayTemplates.filter((t) => t.auto_open_after_work && !isFilledForTemplate(t));
          if (afterWorkTemplates.length > 0) {
            workEndedRef.current = true;
            setConfirmAction(null);
            setTimeout(() => openFillForm(afterWorkTemplates[0]), 500);
            return;
          }
          setSession(null); setElapsed(0); setBreakElapsed(0); fetchData(); fetchMyHistory();
        } catch (err) {
          const msg = getErrorMessage(err);
          setConfirmAction({
            title: 'შეცდომა',
            message: msg,
            type: 'delete',
            confirmText: 'კარგი',
            onConfirm: () => setConfirmAction(null),
          });
          return;
        }
        setConfirmAction(null);
      },
    });
  };

  const hideAttachment = (fa) => {
    setConfirmAction({
      title: 'დანართის წაშლა',
      message: `ნამდვილად გსურთ "${fa.template_title || 'დანართი'}" წაშლა?`,
      type: 'delete',
      confirmText: 'წაშლა',
      onConfirm: async () => {
        try {
          await api.put(`/filled-attachments/${fa.id}/hide`);
          fetchData();
        } catch (err) {
          const msg = getErrorMessage(err);
          setConfirmAction({
            title: 'შეცდომა', message: msg, type: 'delete', confirmText: 'კარგი',
            onConfirm: () => setConfirmAction(null),
          });
          return;
        }
        setConfirmAction(null);
      },
    });
  };

  const startBreak = () => {
    setConfirmAction({
      title: 'შესვენება',
      message: 'ნამდვილად გსურთ შესვენებაზე გასვლა?',
      type: 'warning',
      confirmText: 'შესვენება',
      onConfirm: async () => {
        try {
          const { data } = await api.post('/work/break/start');
          setSession((s) => ({ ...s, status: 'break', breaks: [...(s.breaks || []), data] }));
        } catch { /* ignored */ }
        setConfirmAction(null);
      },
    });
  };

  const endBreak = async () => {
    try {
      await api.put('/work/break/end');
      setSession((s) => {
        const breaks = [...(s.breaks || [])];
        const last = breaks.length - 1;
        if (last >= 0 && !breaks[last].ended_at) breaks[last] = { ...breaks[last], ended_at: new Date().toISOString() };
        return { ...s, status: 'active', breaks };
      });
    } catch { /* ignored */ }
  };

  // Calendar helpers
  const getMonday = (d) => {
    const date = new Date(d);
    const day = date.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    date.setDate(date.getDate() + diff);
    date.setHours(0, 0, 0, 0);
    return date;
  };

  const getWeekDates = (mondayDate, allowedDays) => {
    const days = {};
    (allowedDays || [0,1,2,3,4,5,6]).forEach((dayIdx) => {
      const d = new Date(mondayDate);
      d.setDate(d.getDate() + dayIdx);
      days[dayIdx] = d;
    });
    return days;
  };

  const DAY_NAMES = ['ორშაბათი','სამშაბათი','ოთხშაბათი','ხუთშაბათი','პარასკევი','შაბათი','კვირა'];

  const formatDateShort = (d) => {
    return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}`;
  };

  // Fill attachment
  const openFillForm = (template) => {
    setSelectedTemplate(template);
    const hv = {};
    (template.header_fields || []).forEach((f, i) => {
      if (f.type === 'auto_user') hv[i] = `${user.first_name} ${user.last_name} - ${user.personal_number}`;
      else if (f.type === 'auto_user_phone') hv[i] = user.phone || '';
      else if (f.type === 'client_dropdown' && clients.length === 1) hv[i] = String(clients[0].id);
      else hv[i] = '';
    });
    setHeaderValues(hv);

    if (template.type === 'calendar') {
      const baseTime = serverTime || new Date();
      const monday = getMonday(baseTime);
      monday.setDate(monday.getDate() + 7); // Next week's Monday
      setCalendarWeekStart(monday);
      const days = {};
      (template.allowed_days || [0,1,2,3,4,5,6]).forEach((dayIdx) => {
        days[dayIdx] = [{}]; // One empty entry per day
      });
      setCalendarDays(days);
      setRowData([]);
      setFullRowData({});
    } else if (template.type === 'weekly') {
      const monday = getMonday(new Date());
      monday.setDate(monday.getDate() + 7); // Next week's Monday
      setCalendarWeekStart(monday);
      // Pre-populate rows for each day of next week (Mon-Sun) - array of entries per day
      const days = {};
      [0,1,2,3,4,5,6].forEach((dayIdx) => {
        days[dayIdx] = [{}];
      });
      setCalendarDays(days);
      setRowData([]);
      setFullRowData({});
    } else {
      setRowData([createEmptyRow(template)]);
      const frd = {};
      (template.columns || []).forEach((c, i) => { if (c.fullRow) frd[i] = ['']; });
      setFullRowData(frd);
      setCalendarWeekStart(null);
      setCalendarDays({});
    }

    setFillNote('');
    setFillStartTime(new Date().toISOString());
    setShowFill(true);
  };

  const formatDateTime = (date) => {
    const d = String(date.getDate()).padStart(2, '0');
    const mo = String(date.getMonth() + 1).padStart(2, '0');
    const h = String(date.getHours()).padStart(2, '0');
    const mi = String(date.getMinutes()).padStart(2, '0');
    const sec = String(date.getSeconds()).padStart(2, '0');
    return {
      date: `${d}/${mo}/${date.getFullYear()}`,
      time: `${h}:${mi}:${sec}`,
      full: `${d}/${mo}/${date.getFullYear()} ${h}:${mi}:${sec}`,
    };
  };

  const getNow = () => formatDateTime(new Date());

  const createEmptyRow = (tmpl) => {
    const row = {};
    (tmpl.columns || []).forEach((c, i) => {
      if (c.fullRow) return;
      row[i] = '';
    });
    return row;
  };

  const addRow = () => setRowData([...rowData, createEmptyRow(selectedTemplate)]);
  const updateRow = (ri, ci, val) => {
    const u = [...rowData]; u[ri] = { ...u[ri], [ci]: val }; setRowData(u);
  };
  const removeRow = (ri) => {
    if (rowData.length === 1) return;
    setRowData(rowData.filter((_, i) => i !== ri));
  };

  const saveSignature = () => {
    if (sigRef.current && !sigRef.current.isEmpty()) {
      const dataURL = sigRef.current.toDataURL();
      if (showSignModal.weekly) {
        // Weekly form signature
        const { dayIdx, entryIdx = 0, ci } = showSignModal;
        const u = { ...calendarDays };
        const entries = [...(Array.isArray(u[dayIdx]) ? u[dayIdx] : [u[dayIdx] || {}])];
        entries[entryIdx] = { ...(entries[entryIdx] || {}), [ci]: dataURL };
        u[dayIdx] = entries;
        setCalendarDays(u);
      } else if (showSignModal.weeklyFullRow) {
        // Weekly form fullRow signature
        const { ci } = showSignModal;
        const u = { ...calendarDays };
        u._fullRow = { ...(u._fullRow || {}), [ci]: dataURL };
        setCalendarDays(u);
      } else if (showSignModal.fullRow) {
        const { ci, vi } = showSignModal;
        const items = [...(fullRowData[ci] || [''])];
        items[vi] = dataURL;
        setFullRowData({ ...fullRowData, [ci]: items });
      } else {
        const { ri, ci } = showSignModal;
        updateRow(ri, ci, dataURL);
      }
    }
    setShowSignModal(null);
  };

  const getClientId = () => {
    const hf = selectedTemplate.header_fields || [];
    for (let i = 0; i < hf.length; i++) {
      if (hf[i].type === 'client_dropdown' && headerValues[i]) return parseInt(headerValues[i]);
    }
    return null;
  };

  const submitAttachment = async () => {
    // Get server time for accurate timestamps
    let serverNow;
    try {
      const { data } = await api.get('/work/server-time');
      serverNow = new Date(data.now);
    } catch {
      serverNow = new Date();
    }
    const nowFormatted = formatDateTime(serverNow);
    const cols = selectedTemplate.columns || [];

    // Calculate working duration: session start to now, minus breaks
    let workDuration = 0;
    if (session) {
      const sessionStart = new Date(session.started_at).getTime();
      let totalBreak = 0;
      (session.breaks || []).forEach((b) => {
        if (b.ended_at) {
          totalBreak += new Date(b.ended_at).getTime() - new Date(b.started_at).getTime();
        }
      });
      workDuration = Math.floor((serverNow.getTime() - sessionStart - totalBreak) / 1000);
    } else {
      workDuration = Math.floor((serverNow.getTime() - new Date(fillStartTime).getTime()) / 1000);
    }
    const durationStr = formatTime(workDuration > 0 ? workDuration : 0);

    // Session start time formatted
    const sessionStartFormatted = session ? formatDateTime(new Date(session.started_at)) : formatDateTime(serverNow);

    // Auto-fill duration and datetime columns in rows
    const finalRows = rowData.map((row) => {
      const r = { ...row };
      cols.forEach((c, i) => {
        if (c.fullRow) return;
        if (c.type === 'duration') r[i] = durationStr;
        if (c.type === 'datetime') r[i] = nowFormatted.full;
        if (c.type === 'worktime') r[i] = `${sessionStartFormatted.time} - ${nowFormatted.time}`;
      });
      return r;
    });

    // Auto-fill duration and datetime in fullRowData
    const finalFullRows = { ...fullRowData };
    cols.forEach((c, i) => {
      if (c.fullRow && c.type === 'duration') {
        finalFullRows[i] = [durationStr];
      }
      if (c.fullRow && c.type === 'datetime') {
        finalFullRows[i] = [nowFormatted.full];
      }
      if (c.fullRow && c.type === 'worktime') {
        finalFullRows[i] = [`${sessionStartFormatted.time} - ${nowFormatted.time}`];
      }
    });

    const isCalendar = selectedTemplate.type === 'calendar';
    const isWeekly = selectedTemplate.type === 'weekly';

    // Build final header values with auto-filled client_address and client_phone
    const finalHeaderValues = { ...headerValues };
    const hfList = selectedTemplate.header_fields || [];
    const selectedClient = (() => {
      for (let j = 0; j < hfList.length; j++) {
        if (hfList[j].type === 'client_dropdown' && headerValues[j]) {
          return clients.find(c => String(c.id) === String(headerValues[j]));
        }
      }
      return null;
    })();
    hfList.forEach((f, i) => {
      if (f.type === 'client_address') finalHeaderValues[i] = selectedClient?.address || '';
      if (f.type === 'client_phone') finalHeaderValues[i] = selectedClient?.mobile || '';
      if (f.type === 'auto_user_phone') finalHeaderValues[i] = user.phone || '';
    });

    try {
      const payload = {
        template_id: selectedTemplate.id,
        client_id: getClientId(),
        header_values: finalHeaderValues,
        note: fillNote,
        started_at: fillStartTime,
        duration_seconds: workDuration > 0 ? workDuration : 0,
        session_id: session?.id || null,
      };

      if (isCalendar) {
        payload.calendar_data = {
          weekStart: calendarWeekStart ? `${calendarWeekStart.getFullYear()}-${String(calendarWeekStart.getMonth()+1).padStart(2,'0')}-${String(calendarWeekStart.getDate()).padStart(2,'0')}` : null,
          days: calendarDays,
        };
        payload.row_data = [{}]; // placeholder for validation
      } else if (isWeekly) {
        // Clean up internal keys before sending
        const cleanDays = JSON.parse(JSON.stringify(calendarDays));
        const skipDays = cleanDays._skipDays || {};
        delete cleanDays._skipDays;
        // Mark skipped days with empty data, flatten arrays to plain objects
        Object.keys(cleanDays).forEach((d) => {
          if (d === '_fullRow') return;
          if (skipDays[d]) { cleanDays[d] = { _skipped: true }; return; }
          const val = cleanDays[d];
          if (Array.isArray(val)) cleanDays[d] = val[0] || {};
        });
        payload.calendar_data = {
          type: 'weekly',
          weekStart: calendarWeekStart ? `${calendarWeekStart.getFullYear()}-${String(calendarWeekStart.getMonth()+1).padStart(2,'0')}-${String(calendarWeekStart.getDate()).padStart(2,'0')}` : null,
          days: cleanDays,
        };
        payload.row_data = [{}]; // placeholder for validation
      } else {
        payload.row_data = finalRows;
        payload.full_row_data = finalFullRows;
      }

      await api.post('/filled-attachments', payload);
      // Check if all templates are now filled in this session
      const updatedFilled = await api.get('/filled-attachments/my');
      setFilledList(updatedFilled.data);
      const filledInSession = new Set(updatedFilled.data.filter((fa) => fa.session_id === session?.id).map((fa) => fa.template_id));
      // Weekly frequency: check this week
      const wkNow = serverTime || new Date();
      const wkStart = new Date(wkNow);
      wkStart.setDate(wkStart.getDate() - ((wkStart.getDay() + 6) % 7));
      wkStart.setHours(0, 0, 0, 0);
      const filledThisWeek = new Set(updatedFilled.data.filter((fa) => new Date(fa.completed_at || fa.created_at) >= wkStart).map((fa) => fa.template_id));
      const isFilledCheck = (t) => t.fill_frequency === 'weekly' ? filledThisWeek.has(t.id) : filledInSession.has(t.id);

      setShowConfirm(false);
      setShowFill(false);

      // If work already ended (filling after-work templates)
      if (workEndedRef.current) {
        const moreAfterWork = todayTemplates.filter((t) => t.auto_open_after_work && !isFilledCheck(t));
        if (moreAfterWork.length > 0) {
          setTimeout(() => openFillForm(moreAfterWork[0]), 500);
        } else {
          workEndedRef.current = false;
          setSession(null); setElapsed(0); setBreakElapsed(0);
        }
        fetchData();
        return;
      }

      // Exclude after-work templates from auto-end check
      const allDone = todayTemplates.filter((t) => !t.auto_open_after_work).every((t) => isFilledCheck(t));

      if (allDone && session) {
        try { await api.put('/work/end'); } catch { /* ignored */ }
        // Check for after-work templates
        const afterWorkTemplates = todayTemplates.filter((t) => t.auto_open_after_work && !isFilledCheck(t));
        if (afterWorkTemplates.length > 0) {
          workEndedRef.current = true;
          setTimeout(() => openFillForm(afterWorkTemplates[0]), 500);
          fetchData();
          return;
        }
        setSession(null); setElapsed(0); setBreakElapsed(0);
      }
      fetchData();
    } catch { /* ignored */ }
  };

  const openView = async (fa) => {
    try {
      const { data } = await api.get(`/filled-attachments/${fa.id}`);
      setViewData(data);
      setShowView(true);
    } catch { /* ignored */ }
  };

  const openClientDetail = async (client) => {
    setShowClientDetail(client);
    setClientDateFilter('');
    setClientDetailLoading(true);
    try {
      const { data } = await api.get(`/filled-attachments/by-client/${client.id}`);
      setClientAttachments(data);
    } catch { /* ignored */ }
    finally { setClientDetailLoading(false); }
  };

  const filteredClientAttachments = clientAttachments.filter((fa) => {
    if (!clientDateFilter) return true;
    const faDate = new Date(fa.completed_at).toISOString().split('T')[0];
    return faDate === clientDateFilter;
  });

  // Filter templates by active + today's day + missed days (using server time)
  const todayDay = (() => { const d = (serverTime || new Date()).getDay(); return d === 0 ? 6 : d - 1; })(); // 0=Mon..6=Sun
  const todayTemplates = templates
    .filter((t) => {
      if (t.is_active === false) return false;
      const days = t.allowed_days || [0,1,2,3,4,5,6];
      // Normal: template is for today
      if (days.includes(todayDay)) return true;
      // Missed: auto_open_missed + was due in last 7 days + not filled since then
      if (t.auto_open_missed) {
        const ref = serverTime || new Date();
        for (const allowedDay of days) {
          // How many days ago was this allowed day?
          let daysAgo = todayDay - allowedDay;
          if (daysAgo <= 0) daysAgo += 7; // wrap around week boundary
          if (daysAgo > 0 && daysAgo <= 6) {
            const dueDate = new Date(ref);
            dueDate.setDate(dueDate.getDate() - daysAgo);
            dueDate.setHours(0, 0, 0, 0);
            const wasFilled = filledList.some(fa =>
              fa.template_id === t.id &&
              fa.completed_at &&
              new Date(fa.completed_at) >= dueDate
            );
            if (!wasFilled) return true;
          }
        }
      }
      return false;
    })
    .sort((a, b) => (a.priority || 0) - (b.priority || 0));

  // Check if template dependency is satisfied (depends_on template must be filled in current session)
  const isTemplateFillable = useCallback((template) => {
    if (!template.depends_on) return true;
    // If the dependency template is NOT in today's list (including missed), skip the check
    const depInToday = todayTemplates.some(t => t.id === template.depends_on);
    if (!depInToday) return true;
    // Check if dependency is filled - respect fill_frequency
    const depTemplate = todayTemplates.find(t => t.id === template.depends_on);
    if (depTemplate?.fill_frequency === 'weekly') {
      const now = new Date();
      const wkStart = new Date(now);
      wkStart.setDate(wkStart.getDate() - ((wkStart.getDay() + 6) % 7));
      wkStart.setHours(0, 0, 0, 0);
      return filledList.some((fa) => fa.template_id === template.depends_on && new Date(fa.completed_at || fa.created_at) >= wkStart);
    }
    const currentSessionId = session?.id;
    if (!currentSessionId) return false;
    return filledList.some((fa) => fa.template_id === template.depends_on && fa.session_id === currentSessionId);
  }, [session, filledList, todayTemplates]);

  // Auto-open on login (no session needed)
  const loginOpenRef = useRef(false);
  useEffect(() => {
    if (loginOpenRef.current || todayTemplates.length === 0) return;
    const loginOpenable = todayTemplates.filter((t) => t.auto_open_on_login && isTemplateFillable(t));
    if (loginOpenable.length > 0) {
      loginOpenRef.current = true;
      setTimeout(() => openFillForm(loginOpenable[0]), 500);
    }
  }, [todayTemplates, isTemplateFillable]);

  // Auto-open on work start or missed
  const autoOpenRef = useRef(false);
  useEffect(() => {
    if (!session || session.status !== 'active' || autoOpenRef.current) return;

    const currentSessionId = session?.id;
    const filledInSession = new Set(filledList.filter((fa) => fa.session_id === currentSessionId).map((fa) => fa.template_id));
    const sNow = serverTime || new Date();
    const wkStart = new Date(sNow); wkStart.setDate(wkStart.getDate() - ((wkStart.getDay() + 6) % 7)); wkStart.setHours(0, 0, 0, 0);
    const filledThisWeek = new Set(filledList.filter((fa) => new Date(fa.completed_at || fa.created_at) >= wkStart).map((fa) => fa.template_id));
    const isFilledForFreq = (t) => t.fill_frequency === 'weekly' ? filledThisWeek.has(t.id) : filledInSession.has(t.id);
    const autoOpenable = todayTemplates.filter((t) => (t.auto_open_on_start || t.auto_open_missed) && !isFilledForFreq(t) && isTemplateFillable(t));
    if (autoOpenable.length > 0) {
      autoOpenRef.current = true;
      setTimeout(() => openFillForm(autoOpenable[0]), 500);
    }
  }, [session, todayTemplates, filledList, isTemplateFillable]);

  // Track work-ended state for after-work auto-open
  const workEndedRef = useRef(false);

  const printAttachment = () => {
    window.print();
  };

  const workingTime = elapsed - breakElapsed;

  return (
    <div className="page user-dashboard">
      {/* Timer section */}
      <div className="timer-section">
        <div className="timer-card">
          <div className="timer-greeting">გამარჯობა, {user.first_name}!</div>
          {!session ? (
            <div className="timer-start">
              <button className="start-btn" onClick={startWork}><FiPlay /> სამუშაოს დაწყება</button>
            </div>
          ) : (
            <>
              <div className="timer-display">
                <div className="timer-main">
                  <span className="timer-label">სამუშაო დრო</span>
                  <span className="timer-value">{formatTime(workingTime > 0 ? workingTime : 0)}</span>
                </div>
                {breakElapsed > 0 && (
                  <div className="timer-break-info">
                    <FiCoffee /> შესვენება: {formatTime(breakElapsed)}
                  </div>
                )}
              </div>
              <div className="timer-status">
                {session.status === 'break' ? (
                  <span className="status-badge status-break"><FiCoffee /> შესვენებაზე</span>
                ) : (
                  <span className="status-badge status-active"><FiClock /> მუშაობს</span>
                )}
              </div>
              <div className="timer-actions">
                {session.status === 'active' ? (
                  <button className="break-btn" onClick={startBreak}><FiCoffee /> შესვენება</button>
                ) : (
                  <button className="resume-btn" onClick={endBreak}><FiPlay /> გაგრძელება</button>
                )}
                <button className="end-btn" onClick={endWork}><FiSquare /> დასრულება</button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* My Work History */}
      <div className="section my-history-section">
        <div className="history-header" onClick={() => setHistoryOpen(!historyOpen)}>
          <h3 className="section-title" style={{ margin: 0 }}>სამუშაო ისტორია</h3>
          <span className={`history-toggle ${historyOpen ? 'open' : ''}`}>▾</span>
        </div>
        {historyOpen && <>
        <div className="my-stats-periods">
          {[
            { key: 'today', label: 'დღეს' },
            { key: 'week', label: 'კვირა' },
            { key: 'month', label: 'თვე' },
            { key: 'all', label: 'სულ' },
          ].map((p) => {
            let dateHint = '';
            const now = new Date();
            const fmtD = (dt) => dt.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
            if (p.key === 'today') dateHint = fmtD(now);
            else if (p.key === 'week') {
              const day = now.getDay() || 7;
              const mon = new Date(now); mon.setDate(now.getDate() - day + 1);
              const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
              dateHint = `${fmtD(mon)} - ${fmtD(sun)}`;
            } else if (p.key === 'month') {
              const start = new Date(now.getFullYear(), now.getMonth(), 1);
              const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
              dateHint = `${fmtD(start)} - ${fmtD(end)}`;
            }
            return (
              <button key={p.key} className={`my-stats-period-btn ${historyPeriod === p.key ? 'active' : ''}`} onClick={() => setHistoryPeriod(p.key)}>
                {p.label}
                {dateHint && <span className="my-stats-date-hint">{dateHint}</span>}
              </button>
            );
          })}
        </div>
        <div className={`my-stats-date-range ${historyPeriod === 'custom' ? 'active' : ''}`}>
          <Calendar compact value={historyDateFrom} onChange={(v) => { setHistoryDateFrom(v); setHistoryPeriod('custom'); }} placeholder="დან" max={historyDateTo || todayStr2} />
          <span className="my-stats-drp-sep">—</span>
          <Calendar compact value={historyDateTo} onChange={(v) => { setHistoryDateTo(v); setHistoryPeriod('custom'); }} placeholder="მდე" max={todayStr2} min={historyDateFrom || undefined} />
        </div>
        {(() => {
          const fmtHrs = (sec) => {
            const s = Math.max(0, Math.round(Number(sec) || 0));
            const h = Math.floor(s / 3600);
            const m = Math.floor((s % 3600) / 60);
            if (h === 0) return `${m} წთ`;
            return `${h} სთ ${m} წთ`;
          };
          const totalWork = myHistory.reduce((sum, s) => sum + Math.max(0, Number(s.net_work_seconds) || 0), 0);
          const totalBreak = myHistory.reduce((sum, s) => sum + Math.max(0, Number(s.break_seconds) || 0), 0);
          return (
            <>
              {myHistory.length > 0 && (
                <div className="history-summary">
                  <div className="hs-item hs-work">
                    <FiClock />
                    <span>{fmtHrs(totalWork)}</span>
                  </div>
                  <div className="hs-item hs-break">
                    <FiCoffee />
                    <span>{fmtHrs(totalBreak)}</span>
                  </div>
                  <div className="hs-item hs-count">
                    <FiCalendar />
                    <span>{myHistory.length} სესია</span>
                  </div>
                </div>
              )}
              {myHistory.length === 0 ? (
                <div className="empty-hint">ამ პერიოდში სესიები არ არის</div>
              ) : (
                <div className="history-list">
                  {myHistory.map((s) => {
                    const startDt = new Date(s.started_at);
                    const endDt = new Date(s.ended_at);
                    const dayNames = ['კვ', 'ორშ', 'სამშ', 'ოთხ', 'ხუთ', 'პარ', 'შაბ'];
                    const dayName = dayNames[startDt.getDay()];
                    const dateStr = `${dayName}, ${startDt.getDate().toString().padStart(2, '0')}/${(startDt.getMonth() + 1).toString().padStart(2, '0')}`;
                    const startTime = startDt.toLocaleTimeString('ka-GE', { hour: '2-digit', minute: '2-digit', hour12: false });
                    const endTime = endDt.toLocaleTimeString('ka-GE', { hour: '2-digit', minute: '2-digit', hour12: false });
                    const netSec = Math.max(0, Math.round(Number(s.net_work_seconds) || 0));
                    const breakSec = Math.max(0, Math.round(Number(s.break_seconds) || 0));
                    return (
                      <div key={s.id} className="history-card">
                        <div className="hc-left">
                          <span className="hc-date">{dateStr}</span>
                          <span className="hc-time">{startTime} — {endTime}</span>
                        </div>
                        <div className="hc-right">
                          <span className="hc-work">{fmtHrs(netSec)}</span>
                          {breakSec > 0 && <span className="hc-break">{fmtHrs(breakSec)} შესვ.</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          );
        })()}
        </>}
      </div>

      {/* Quick actions - always show active templates */}
      {todayTemplates.length > 0 && (() => {
        const currentSessionId = session?.id;
        const filledInSession = new Set(filledList.filter((fa) => fa.session_id === currentSessionId).map((fa) => fa.template_id));
        // For weekly frequency, check if filled this week
        const sNow = serverTime || new Date();
        const weekStart = new Date(sNow);
        weekStart.setDate(weekStart.getDate() - ((weekStart.getDay() + 6) % 7)); // Monday
        weekStart.setHours(0, 0, 0, 0);
        const filledThisWeekIds = new Set(filledList.filter((fa) => new Date(fa.completed_at || fa.created_at) >= weekStart).map((fa) => fa.template_id));
        return (
          <div className="section">
            <h3 className="section-title">დანართის შევსება</h3>
            <div className="template-pick-grid">
              {todayTemplates.map((t, i) => {
                const isFilled = t.fill_frequency === 'weekly' ? filledThisWeekIds.has(t.id) : filledInSession.has(t.id);
                const canFill = isTemplateFillable(t);
                const depTemplate = t.depends_on ? templates.find(dt => dt.id === t.depends_on) : null;
                return (
                  <button key={t.id} className={`template-pick-btn ${isFilled ? 'filled' : ''} ${!canFill && !isFilled ? 'locked' : ''}`} onClick={() => openFillForm(t)} disabled={isFilled || !session || session.status === 'completed' || !canFill}>
                    <span className="tpb-num">{i + 1}</span>
                    {(t.type === 'calendar' || t.type === 'weekly') ? <FiCalendar /> : <FiFileText />} {t.title}
                    {isFilled && <span className="tpb-done">✓</span>}
                    {!canFill && !isFilled && depTemplate && <span className="tpb-locked">({depTemplate.title} შეავსეთ ჯერ)</span>}
                  </button>
                );
              })}
              {!session && <span className="empty-hint">დანართის შესავსებად დაიწყეთ სამუშაო</span>}
            </div>
          </div>
        );
      })()}

      {/* Assigned clients */}
      <div className="section">
        <h3 className="section-title">ჩემი ბენეფიციარები</h3>
        <div className="my-clients-list">
          {clients.map((c) => (
            <div key={c.id} className="my-client-card" onClick={() => openClientDetail(c)}>
              <div className="mc-avatar">{c.first_name[0]}{c.last_name[0]}</div>
              <div className="mc-info">
                <span className="mc-name">{c.first_name} {c.last_name}</span>
                <span className="mc-pn">{c.personal_number}</span>
              </div>
              <FiEye className="mc-arrow" />
            </div>
          ))}
          {clients.length === 0 && <span className="empty-hint">ბენეფიციარები არ არის მიმაგრებული</span>}
        </div>
      </div>

      {/* Filled attachments history - grouped by session */}
      <div className="section">
        <h3 className="section-title">შევსებული დანართები</h3>
        <div className="filled-list">
          {(() => {
            // Group by session_id
            const groups = [];
            const sessionMap = new Map();
            filledList.forEach((fa) => {
              const key = fa.session_id || `no-session-${fa.id}`;
              if (!sessionMap.has(key)) {
                sessionMap.set(key, []);
                groups.push(key);
              }
              sessionMap.get(key).push(fa);
            });
            if (groups.length === 0) return <div className="empty-hint">შევსებული დანართები არ არის</div>;
            const perPage = 10;
            const totalItems = filledList.length;
            const paginatedItems = filledList.slice(0, filledPage * perPage);
            // Regroup paginated items
            const pGroups = [];
            const pMap = new Map();
            paginatedItems.forEach((fa) => {
              const key = fa.session_id || `no-session-${fa.id}`;
              if (!pMap.has(key)) { pMap.set(key, []); pGroups.push(key); }
              pMap.get(key).push(fa);
            });
            return (
              <>
                {pGroups.map((key) => {
                  const items = pMap.get(key);
                  const first = items[0];
                  const dateStr = new Date(first.completed_at).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
                  return (
                    <div key={key} className="filled-session-group">
                      <div className="fsg-header">
                        <FiClock className="fsg-icon" />
                        <span className="fsg-date">{dateStr}</span>
                        <span className="fsg-count">{items.length} დანართი</span>
                      </div>
                      {items.map((fa) => (
                        <div key={fa.id} className="filled-card" onClick={() => openView(fa)}>
                          <div className="fc-icon"><FiFileText /></div>
                          <div className="fc-info">
                            <span className="fc-title">{fa.template_title || 'დანართი'}</span>
                            <span className="fc-meta">
                              {fa.client_first_name && `${fa.client_first_name} ${fa.client_last_name} | `}
                              {(() => { const d = new Date(fa.completed_at); return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}, ${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`; })()}
                            </span>
                          </div>
                          <div className="fc-actions"><FiEye /></div>
                        </div>
                      ))}
                    </div>
                  );
                })}
                {paginatedItems.length < totalItems && (
                  <button className="filled-load-more" onClick={() => setFilledPage(filledPage + 1)}>
                    მეტის ჩვენება ({totalItems - paginatedItems.length} დარჩენილი)
                  </button>
                )}
              </>
            );
          })()}
        </div>
      </div>

      {/* Fill form modal */}
      {showFill && selectedTemplate && (
        <div className="modal-overlay" onClick={() => setShowFill(false)}>
          <div className="modal attachment-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{selectedTemplate.title}</h3>
              <span className="fill-date-badge">
                <FiCalendar />
                {(() => { const d = serverTime || new Date(); const days = ['კვირა','ორშაბათი','სამშაბათი','ოთხშაბათი','ხუთშაბათი','პარასკევი','შაბათი']; return `${days[d.getDay()]}, ${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`; })()}
              </span>
              <button className="btn-icon" onClick={() => setShowFill(false)}><FiX /></button>
            </div>
            <div className="fill-form">
              {/* Header fields */}
              {(selectedTemplate.header_fields || []).map((f, i) => {
                // Auto-fill client_address and client_phone from selected client
                const getSelectedClient = () => {
                  const hf = selectedTemplate.header_fields || [];
                  for (let j = 0; j < hf.length; j++) {
                    if (hf[j].type === 'client_dropdown' && headerValues[j]) {
                      return clients.find(c => String(c.id) === String(headerValues[j]));
                    }
                  }
                  return null;
                };

                if (f.type === 'client_address') {
                  const cl = getSelectedClient();
                  const val = cl ? (cl.address || 'მისამართი არ არის') : '';
                  return (
                    <div key={i} className="form-group">
                      <label>{f.label}</label>
                      <input value={val} readOnly className="auto-field" />
                    </div>
                  );
                }
                if (f.type === 'client_phone') {
                  const cl = getSelectedClient();
                  const val = cl ? (cl.mobile || 'ტელეფონი არ არის') : '';
                  return (
                    <div key={i} className="form-group">
                      <label>{f.label}</label>
                      <input value={val} readOnly className="auto-field" />
                    </div>
                  );
                }

                return (
                  <div key={i} className="form-group">
                    <label>{f.label}</label>
                    {f.type === 'client_dropdown' ? (
                      <Dropdown
                        value={headerValues[i] || ''}
                        onChange={(val) => setHeaderValues({ ...headerValues, [i]: val })}
                        placeholder="აირჩიეთ"
                        options={[
                          { value: '', label: 'აირჩიეთ' },
                          ...clients.map((c) => ({ value: String(c.id), label: `${c.first_name} ${c.last_name} - ${c.personal_number}` })),
                        ]}
                      />
                    ) : f.type === 'auto_user' ? (
                      <input value={headerValues[i] || ''} readOnly className="auto-field" />
                    ) : f.type === 'auto_user_phone' ? (
                      <input value={headerValues[i] || ''} readOnly className="auto-field" placeholder="ტელეფონი არ არის მითითებული" />
                    ) : (
                      <input value={headerValues[i] || ''} onChange={(e) => setHeaderValues({ ...headerValues, [i]: e.target.value })} />
                    )}
                  </div>
                );
              })}

              {/* Calendar fill form */}
              {selectedTemplate.type === 'calendar' && calendarWeekStart && (() => {
                const cols = (selectedTemplate.columns || []).map((c, i) => ({ ...c, idx: i }));
                const weekDates = getWeekDates(calendarWeekStart, selectedTemplate.allowed_days);
                const nextMonday = getMonday(serverTime || new Date());
                nextMonday.setDate(nextMonday.getDate() + 7);
                const canGoPrev = calendarWeekStart.getTime() > nextMonday.getTime();
                const changeWeek = (dir) => {
                  if (dir === -1 && !canGoPrev) return;
                  const d = new Date(calendarWeekStart);
                  d.setDate(d.getDate() + dir * 7);
                  setCalendarWeekStart(d);
                };
                const updateCalEntry = (dayIdx, entryIdx, colIdx, val) => {
                  const u = { ...calendarDays };
                  u[dayIdx] = [...(u[dayIdx] || [{}])];
                  u[dayIdx][entryIdx] = { ...u[dayIdx][entryIdx], [colIdx]: val };
                  setCalendarDays(u);
                };
                const addCalEntry = (dayIdx) => {
                  const u = { ...calendarDays };
                  u[dayIdx] = [...(u[dayIdx] || []), {}];
                  setCalendarDays(u);
                };
                const removeCalEntry = (dayIdx, entryIdx) => {
                  const u = { ...calendarDays };
                  u[dayIdx] = (u[dayIdx] || []).filter((_, i) => i !== entryIdx);
                  if (u[dayIdx].length === 0) u[dayIdx] = [{}];
                  setCalendarDays(u);
                };

                const endOfWeek = new Date(calendarWeekStart);
                endOfWeek.setDate(endOfWeek.getDate() + 6);

                return (
                  <div className="cal-fill">
                    {/* Week picker */}
                    <div className="cal-week-picker">
                      <button type="button" className={`cal-week-nav ${!canGoPrev ? 'disabled' : ''}`} onClick={() => changeWeek(-1)} disabled={!canGoPrev}>‹</button>
                      <span className="cal-week-label">
                        {formatDateShort(calendarWeekStart)} — {formatDateShort(endOfWeek)}, {calendarWeekStart.getFullYear()}
                      </span>
                      <button type="button" className="cal-week-nav" onClick={() => changeWeek(1)}>›</button>
                    </div>

                    {/* Day sections */}
                    {Object.keys(weekDates).sort((a, b) => a - b).map((dayIdx) => {
                      const date = weekDates[dayIdx];
                      const entries = calendarDays[dayIdx] || [{}];
                      return (
                        <div key={dayIdx} className="cal-day-section">
                          <div className="cal-day-header">
                            <span className="cal-day-name">{DAY_NAMES[dayIdx]}</span>
                            <span className="cal-day-date">{date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}</span>
                            <span className="cal-day-count">{entries.filter(e => Object.values(e).some(v => v)).length} ჩანაწერი</span>
                          </div>
                          <div className="cal-day-entries">
                            {entries.map((entry, ei) => (
                              <div key={ei} className="cal-entry">
                                <span className="cal-entry-num">{ei + 1}</span>
                                <div className="cal-entry-fields">
                                  {cols.map((c) => (
                                    <div key={c.idx} className="cal-entry-field">
                                      <label className="cal-field-label">{c.name}</label>
                                      {c.type === 'activity' ? (
                                        <Dropdown compact value={entry[c.idx] || ''} onChange={(val) => updateCalEntry(parseInt(dayIdx), ei, c.idx, val)} placeholder="აირჩიეთ აქტივობა" options={[{ value: '', label: 'აირჩიეთ აქტივობა' }, ...activities.filter((a) => !a.template_id || Number(a.template_id) === Number(selectedTemplate.id)).map((a) => ({ value: a.name, label: a.name }))]} />
                                      ) : c.type === 'dropdown' ? (
                                        <Dropdown compact value={entry[c.idx] || ''} onChange={(val) => updateCalEntry(parseInt(dayIdx), ei, c.idx, val)} placeholder="აირჩიეთ" options={[{ value: '', label: 'აირჩიეთ' }, ...(c.options || []).filter(o => o.trim()).map((o) => ({ value: o, label: o }))]} />
                                      ) : c.type === 'datetime' || c.type === 'worktime' || c.type === 'duration' ? (
                                        <span className="auto-field-label">ავტო</span>
                                      ) : (
                                        <input className="fill-input" value={entry[c.idx] || ''} onChange={(e) => updateCalEntry(parseInt(dayIdx), ei, c.idx, e.target.value)} placeholder={c.name} />
                                      )}
                                    </div>
                                  ))}
                                </div>
                                {entries.length > 1 && (
                                  <button type="button" className="btn-icon btn-danger btn-xs" onClick={() => removeCalEntry(parseInt(dayIdx), ei)}><FiX /></button>
                                )}
                              </div>
                            ))}
                            <button type="button" className="cal-add-entry" onClick={() => addCalEntry(parseInt(dayIdx))}>
                              <FiPlus /> ჩანაწერი
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}

              {/* Weekly plan fill form — auto-populated days with dates */}
              {selectedTemplate.type === 'weekly' && calendarWeekStart && (() => {
                const cols = (selectedTemplate.columns || []).map((c, i) => ({ ...c, idx: i }));
                const autoTypes = ['datetime', 'worktime', 'duration'];
                // Day indices 0-6 (Mon-Sun)
                const rowKeys = Object.keys(calendarDays).filter(k => k !== '_fullRow' && k !== '_skipDays').sort((a, b) => a - b);

                const updateWeeklyField = (dayIdx, entryIdx, colIdx, val) => {
                  const u = { ...calendarDays };
                  const entries = [...(u[dayIdx] || [{}])];
                  entries[entryIdx] = { ...(entries[entryIdx] || {}), [colIdx]: val };
                  u[dayIdx] = entries;
                  setCalendarDays(u);
                };

                const addWeeklyEntry = (dayIdx) => {
                  const u = { ...calendarDays };
                  u[dayIdx] = [...(u[dayIdx] || [{}]), {}];
                  setCalendarDays(u);
                };

                const removeWeeklyEntry = (dayIdx, entryIdx) => {
                  const u = { ...calendarDays };
                  const entries = [...(u[dayIdx] || [{}])];
                  if (entries.length <= 1) return;
                  entries.splice(entryIdx, 1);
                  u[dayIdx] = entries;
                  setCalendarDays(u);
                };

                const endOfWeek = new Date(calendarWeekStart);
                endOfWeek.setDate(endOfWeek.getDate() + 6);

                // Get date for a day index
                const getDayDate = (dayIdx) => {
                  const d = new Date(calendarWeekStart);
                  d.setDate(d.getDate() + parseInt(dayIdx));
                  return d;
                };

                const renderWeeklyCell = (c, dayIdx, entryIdx, entryData) => {
                  if (c.type === 'signature') {
                    return entryData[c.idx] ? (
                      <img src={entryData[c.idx]} alt="" className="sig-thumb" onClick={() => setShowSignModal({ weekly: true, dayIdx: parseInt(dayIdx), entryIdx, ci: c.idx })} />
                    ) : (
                      <button type="button" className="sign-btn" onClick={() => setShowSignModal({ weekly: true, dayIdx: parseInt(dayIdx), entryIdx, ci: c.idx })}>✍</button>
                    );
                  }
                  if (c.type === 'activity') {
                    const actOpts = activities.filter((a) => !a.template_id || Number(a.template_id) === Number(selectedTemplate.id));
                    return <ActivityMultiSelect options={actOpts} value={entryData[c.idx] || ''} onChange={(val) => updateWeeklyField(parseInt(dayIdx), entryIdx, c.idx, val)} />;
                  }
                  if (c.type === 'dropdown') {
                    const opts = (c.options || []).filter(o => o.trim());
                    return <Dropdown compact value={entryData[c.idx] || ''} onChange={(val) => updateWeeklyField(parseInt(dayIdx), entryIdx, c.idx, val)} placeholder="აირჩიეთ" options={[{ value: '', label: 'აირჩიეთ' }, ...opts.map((o) => ({ value: o, label: o }))]} />;
                  }
                  if (c.type === 'datetime') return <span className="auto-field-label">ავტო</span>;
                  if (c.type === 'worktime') {
                    return <TimePicker value={entryData[c.idx] || ''} onChange={(val) => updateWeeklyField(parseInt(dayIdx), entryIdx, c.idx, val)} />;
                  }
                  if (c.type === 'duration') return <span className="auto-field-label">ავტო</span>;
                  return <input className="fill-input" value={entryData[c.idx] || ''} onChange={(e) => updateWeeklyField(parseInt(dayIdx), entryIdx, c.idx, e.target.value)} />;
                };

                const renderWeeklyFullRowCell = (c) => {
                  const val = calendarDays._fullRow?.[c.idx] || '';
                  if (c.type === 'signature') {
                    return (
                      <div className="fullrow-cell">
                        <div className="fullrow-cell-item">
                          {val ? <img src={val} alt="" className="sig-thumb" onClick={() => setShowSignModal({ weeklyFullRow: true, ci: c.idx })} />
                            : <button type="button" className="sign-btn" onClick={() => setShowSignModal({ weeklyFullRow: true, ci: c.idx })}>✍</button>}
                        </div>
                      </div>
                    );
                  }
                  if (c.type === 'worktime') {
                    const upd = (v) => { const u = { ...calendarDays }; u._fullRow = { ...(u._fullRow || {}), [c.idx]: v }; setCalendarDays(u); };
                    return <TimePicker value={val} onChange={upd} />;
                  }
                  if (c.type === 'datetime' || c.type === 'duration') {
                    return <span className="auto-field-label">ავტო</span>;
                  }
                  return (
                    <div className="fullrow-cell">
                      <div className="fullrow-cell-item">
                        <input className="fill-input" value={val} onChange={(e) => {
                          const u = { ...calendarDays };
                          u._fullRow = { ...(u._fullRow || {}), [c.idx]: e.target.value };
                          setCalendarDays(u);
                        }} placeholder={c.name} />
                      </div>
                    </div>
                  );
                };

                return (
                  <>
                    <div className="weekly-week-label">
                      კვირა: {formatDateShort(calendarWeekStart)} — {formatDateShort(endOfWeek)}, {calendarWeekStart.getFullYear()}
                    </div>
                    <div className="fill-table-wrap">
                      <table className="fill-table">
                        <thead><tr>
                          <th style={{width: 30}}></th>
                          <th>დღე</th>
                          <th>თარიღი</th>
                          {cols.map((c) => (<th key={c.idx} style={c.width ? {width: `${c.width}%`} : undefined}>{c.name}{c.required && <span style={{color:'#e53e3e'}}> *</span>}</th>))}
                        </tr></thead>
                        <tbody>
                          {(() => {
                            const totalRowSpan = rowKeys.reduce((sum, dk) => {
                              const e = Array.isArray(calendarDays[dk]) ? calendarDays[dk] : [calendarDays[dk] || {}];
                              return sum + e.length;
                            }, 0);
                            let isFirstRow = true;
                            return rowKeys.map((dayIdx) => {
                              const entries = Array.isArray(calendarDays[dayIdx]) ? calendarDays[dayIdx] : [calendarDays[dayIdx] || {}];
                              const dayDate = getDayDate(dayIdx);
                              const isSkipped = calendarDays._skipDays?.[dayIdx];
                              const toggleSkip = () => {
                                const u = { ...calendarDays };
                                u._skipDays = { ...(u._skipDays || {}), [dayIdx]: !isSkipped };
                                setCalendarDays(u);
                              };
                              return entries.map((entry, ei) => {
                                const showFullRow = isFirstRow && ei === 0;
                                if (ei === 0 && dayIdx === rowKeys[0]) isFirstRow = false;
                                else if (ei === 0) isFirstRow = false;
                                return (
                                  <tr key={`${dayIdx}-${ei}`} style={isSkipped ? {opacity: 0.4} : {}}>
                                    {ei === 0 && (
                                      <td style={{textAlign: 'center', verticalAlign: 'top'}} rowSpan={entries.length}>
                                        <input type="checkbox" className="day-check" checked={!isSkipped} onChange={toggleSkip} />
                                      </td>
                                    )}
                                    {ei === 0 && (
                                      <td className="row-day-name" rowSpan={entries.length} style={{verticalAlign: 'top'}}><strong>{DAY_NAMES[dayIdx]}</strong></td>
                                    )}
                                    {ei === 0 && (
                                      <td className="row-day-date" rowSpan={entries.length} style={{verticalAlign: 'top'}}>{formatDateShort(dayDate)}</td>
                                    )}
                                    {cols.map((c) => {
                                      if (c.fullRow && showFullRow) {
                                        return <td key={c.idx} rowSpan={totalRowSpan} className="fullrow-td">{renderWeeklyFullRowCell(c)}</td>;
                                      }
                                      if (c.fullRow) return null;
                                      return <td key={c.idx}>{isSkipped ? <span className="skip-dash">—</span> : renderWeeklyCell(c, dayIdx, ei, entry)}</td>;
                                    })}
                                  </tr>
                                );
                              });
                            });
                          })()}
                        </tbody>
                      </table>
                    </div>
                  </>
                );
              })()}

              {/* Standard Table - all columns, fullRow merged */}
              {selectedTemplate.type === 'standard' && (() => {
                const cols = (selectedTemplate.columns || []).map((c, i) => ({ ...c, idx: i }));
                const rowCount = rowData.length;
                const renderCell = (c, row, ri) => {
                  if (c.type === 'signature') {
                    return row[c.idx] ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <img src={row[c.idx]} alt="" className="sig-thumb" onClick={() => setShowSignModal({ ri, ci: c.idx })} />
                        <button type="button" className="btn-icon btn-danger btn-xs" onClick={() => updateRow(ri, c.idx, '')}><FiX /></button>
                      </div>
                    ) : (
                      <button type="button" className="sign-btn" onClick={() => setShowSignModal({ ri, ci: c.idx })}>✍</button>
                    );
                  }
                  if (c.type === 'activity') {
                    const actOpts = activities.filter((a) => !a.template_id || Number(a.template_id) === Number(selectedTemplate.id));
                    return <ActivityMultiSelect options={actOpts} value={row[c.idx] || ''} onChange={(val) => updateRow(ri, c.idx, val)} />;
                  }
                  if (c.type === 'dropdown') {
                    const opts = (c.options || []).filter(o => o.trim());
                    return <Dropdown compact value={row[c.idx] || ''} onChange={(val) => updateRow(ri, c.idx, val)} placeholder="აირჩიეთ" options={[{ value: '', label: 'აირჩიეთ' }, ...opts.map((o) => ({ value: o, label: o }))]} />;
                  }
                  if (c.type === 'datetime') {
                    return <span className="auto-field-label">ავტო: თარიღი/დრო</span>;
                  }
                  if (c.type === 'worktime') {
                    return <span className="auto-field-label">ავტო: დაწყ/დასრ</span>;
                  }
                  if (c.type === 'duration') return <span className="auto-field-label">ავტო: ხანგრძლივობა</span>;
                  return <input className="fill-input" value={row[c.idx] || ''} onChange={(e) => updateRow(ri, c.idx, e.target.value)} />;
                };

                const renderFullRowCell = (c) => {
                  const items = fullRowData[c.idx] || [''];
                  return (
                    <div className="fullrow-cell">
                      {items.map((val, vi) => (
                        <div key={vi} className="fullrow-cell-item">
                          {c.type === 'activity' ? (
                            <ActivityMultiSelect options={activities.filter((a) => !a.template_id || Number(a.template_id) === Number(selectedTemplate.id))} value={val} onChange={(v) => { const u = [...items]; u[vi] = v; setFullRowData({ ...fullRowData, [c.idx]: u }); }} />
                          ) : c.type === 'dropdown' ? (
                            <Dropdown compact value={val} onChange={(v) => { const u = [...items]; u[vi] = v; setFullRowData({ ...fullRowData, [c.idx]: u }); }} placeholder="აირჩიეთ" options={[{ value: '', label: 'აირჩიეთ' }, ...(c.options || []).filter(o => o.trim()).map((o) => ({ value: o, label: o }))]} />
                          ) : c.type === 'signature' ? (
                            val ? <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                <img src={val} alt="" className="sig-thumb" onClick={() => setShowSignModal({ fullRow: true, ci: c.idx, vi })} />
                                <button type="button" className="btn-icon btn-danger btn-xs" onClick={() => { const u = [...items]; u[vi] = ''; setFullRowData({ ...fullRowData, [c.idx]: u }); }}><FiX /></button>
                              </div>
                              : <button type="button" className="sign-btn" onClick={() => setShowSignModal({ fullRow: true, ci: c.idx, vi })}>✍</button>
                          ) : c.type === 'datetime' || c.type === 'worktime' || c.type === 'duration' ? (
                            <span className="auto-field-label">ავტო</span>
                          ) : (
                            <input className="fill-input" value={val} onChange={(e) => { const u = [...items]; u[vi] = e.target.value; setFullRowData({ ...fullRowData, [c.idx]: u }); }} placeholder={c.name} />
                          )}
                        </div>
                      ))}
                    </div>
                  );
                };

                return (
                  <>
                    <div className="fill-table-wrap">
                      <table className="fill-table">
                        <thead><tr>
                          {cols.map((c) => (<th key={c.idx} style={c.width ? {width: `${c.width}%`} : undefined}>{c.name}{c.required && <span style={{color:'#e53e3e'}}> *</span>}</th>))}
                        </tr></thead>
                        <tbody>
                          {rowData.map((row, ri) => (
                            <tr key={ri}>
                              {cols.map((c) => {
                                if (c.fullRow && ri === 0) {
                                  return <td key={c.idx} rowSpan={rowCount} className="fullrow-td">{renderFullRowCell(c)}</td>;
                                }
                                if (c.fullRow) return null;
                                return <td key={c.idx}>{renderCell(c, row, ri)}</td>;
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                );
              })()}

              {selectedTemplate.has_note && (
                <div className="form-group" style={{ marginTop: 16 }}>
                  <label>შენიშვნა</label>
                  <textarea className="fill-textarea" value={fillNote} onChange={(e) => setFillNote(e.target.value)} rows={3} />
                </div>
              )}

              <button type="button" className="btn-primary btn-full" style={{ marginTop: 16 }} onClick={() => setShowConfirm(true)} disabled={(() => {
                const cols = (selectedTemplate.columns || []).map((c, i) => ({ ...c, idx: i }));
                const autoTypes = ['datetime', 'duration'];
                const requiredCols = cols.filter(c => c.required && !autoTypes.includes(c.type));
                const isValidValue = (col, val) => {
                  if (!val?.toString().trim()) return false;
                  if (col.type === 'worktime') {
                    const parts = val.split(' - ');
                    return parts.length === 2 && /^\d{2}:\d{2}$/.test(parts[0]?.trim()) && /^\d{2}:\d{2}$/.test(parts[1]?.trim());
                  }
                  return true;
                };
                if (requiredCols.length === 0) {
                  // Check header fields only
                  const hf = selectedTemplate.header_fields || [];
                  for (let i = 0; i < hf.length; i++) {
                    if (!['auto_user', 'auto_user_phone', 'client_address', 'client_phone'].includes(hf[i].type) && !headerValues[i]?.toString().trim()) return true;
                  }
                  return false;
                }
                // Check header fields (except auto_user)
                const hf = selectedTemplate.header_fields || [];
                for (let i = 0; i < hf.length; i++) {
                  if (!['auto_user', 'auto_user_phone', 'client_address', 'client_phone'].includes(hf[i].type) && !headerValues[i]?.toString().trim()) return true;
                }
                // Standard template
                if (selectedTemplate.type === 'standard' || !selectedTemplate.type) {
                  for (const row of rowData) {
                    for (const c of requiredCols.filter(c => !c.fullRow)) {
                      if (!isValidValue(c, row[c.idx])) return true;
                    }
                  }
                  // Check fullRow required columns
                  for (const c of requiredCols.filter(c => c.fullRow)) {
                    const items = fullRowData[c.idx] || [''];
                    if (items.every(v => !isValidValue(c, v))) return true;
                  }
                }
                // Calendar template
                if (selectedTemplate.type === 'calendar') {
                  for (const dayIdx of Object.keys(calendarDays)) {
                    const entries = calendarDays[dayIdx] || [{}];
                    for (const entry of entries) {
                      for (const c of requiredCols) {
                        if (!isValidValue(c, entry[c.idx])) return true;
                      }
                    }
                  }
                }
                // Weekly template
                if (selectedTemplate.type === 'weekly') {
                  // Check fullRow required columns from _fullRow
                  for (const c of requiredCols.filter(c => c.fullRow)) {
                    const val = calendarDays._fullRow?.[c.idx] || '';
                    if (!isValidValue(c, val)) return true;
                  }
                  // Check per-day required columns (non-fullRow)
                  const rowKeys = Object.keys(calendarDays).filter(k => k !== '_fullRow' && k !== '_skipDays' && !isNaN(parseInt(k)));
                  for (const key of rowKeys) {
                    if (calendarDays._skipDays?.[key]) continue;
                    const entries = Array.isArray(calendarDays[key]) ? calendarDays[key] : [calendarDays[key] || {}];
                    for (const entry of entries) {
                      for (const c of requiredCols.filter(c => !c.fullRow)) {
                        if (!isValidValue(c, entry[c.idx])) return true;
                      }
                    }
                  }
                }
                return false;
              })()}>
                დადასტურება
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm popup */}
      {showConfirm && (
        <ConfirmModal
          title="დანართის დადასტურება"
          message="ნამდვილად ადასტურებთ დანართის შევსებას? დადასტურების შემდეგ ცვლილება შეუძლებელია."
          type="warning"
          confirmText="დადასტურება"
          onConfirm={submitAttachment}
          onCancel={() => setShowConfirm(false)}
        />
      )}

      {/* Signature modal */}
      {showSignModal && (
        <div className="modal-overlay" style={{ zIndex: 3001 }}>
          <div className="modal sign-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header"><h3>ხელმოწერა</h3>
              <button className="btn-icon" onClick={() => setShowSignModal(null)}><FiX /></button></div>
            <div className="sign-area">
              <SignatureCanvas ref={sigRef} canvasProps={{ className: 'sign-canvas' }} penColor="#0f172a" />
              <div className="sign-actions">
                <button type="button" className="btn-secondary" onClick={() => sigRef.current?.clear()}>გასუფთავება</button>
                <button type="button" className="btn-primary" onClick={saveSignature}>შენახვა</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Client detail modal */}
      {showClientDetail && (
        <div className="modal-overlay" onClick={() => setShowClientDetail(null)}>
          <div className="modal attachment-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{showClientDetail.first_name} {showClientDetail.last_name}</h3>
              <button className="btn-icon" onClick={() => setShowClientDetail(null)}><FiX /></button>
            </div>
            <div className="client-detail-content">
              {/* Client info */}
              <div className="cd-info-grid">
                <div className="cd-info-item">
                  <span className="cd-label">პირადი ნომერი</span>
                  <span className="cd-value">{showClientDetail.personal_number}</span>
                </div>
                {showClientDetail.mobile && (
                  <div className="cd-info-item">
                    <span className="cd-label">მობილური</span>
                    <span className="cd-value">{showClientDetail.mobile}</span>
                  </div>
                )}
                {showClientDetail.address && (
                  <div className="cd-info-item">
                    <span className="cd-label">მისამართი</span>
                    <span className="cd-value">{showClientDetail.address}</span>
                  </div>
                )}
                {showClientDetail.birth_date && (
                  <div className="cd-info-item">
                    <span className="cd-label">დაბადების თარიღი</span>
                    <span className="cd-value">{new Date(showClientDetail.birth_date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}</span>
                  </div>
                )}
                {showClientDetail.gender && (
                  <div className="cd-info-item">
                    <span className="cd-label">სქესი</span>
                    <span className="cd-value">{showClientDetail.gender === 'male' ? 'მამრობითი' : 'მდედრობითი'}</span>
                  </div>
                )}
              </div>

              {/* Attachments for this client */}
              <div className="cd-attachments-header">
                <h4>შევსებული დანართები ({filteredClientAttachments.length})</h4>
                <Calendar
                  value={clientDateFilter}
                  onChange={(val) => setClientDateFilter(val)}
                  placeholder="თარიღით ძიება"
                />
              </div>

              {clientDetailLoading ? (
                <div className="loading">იტვირთება...</div>
              ) : (
                <div className="cd-attachments-list">
                  {filteredClientAttachments.map((fa) => (
                    <div key={fa.id} className="filled-card" onClick={() => { setShowClientDetail(null); openView(fa); }}>
                      <div className="fc-icon"><FiFileText /></div>
                      <div className="fc-info">
                        <span className="fc-title">{fa.template_title || 'დანართი'}</span>
                        <span className="fc-meta">{(() => { const d = new Date(fa.completed_at); return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}, ${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`; })()}</span>
                        {fa.duration_seconds && (
                          <span className="fc-duration"><FiClock /> {formatTime(fa.duration_seconds)}</span>
                        )}
                      </div>
                      <FiEye className="fc-actions" />
                    </div>
                  ))}
                  {filteredClientAttachments.length === 0 && (
                    <div className="empty-hint" style={{ padding: 20, textAlign: 'center' }}>
                      {clientDateFilter ? 'ამ თარიღზე დანართები არ არის' : 'დანართები არ არის'}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* View filled attachment */}
      {showView && viewData && (
        <div className="modal-overlay" onClick={() => setShowView(false)}>
          <div className="modal attachment-modal print-area" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header no-print">
              <h3>{viewData.template_title}</h3>
              <span className="fill-date-badge">
                <FiCalendar />
                {(() => { const d = new Date(viewData.completed_at); const days = ['კვირა','ორშაბათი','სამშაბათი','ოთხშაბათი','ხუთშაბათი','პარასკევი','შაბათი']; return `${days[d.getDay()]}, ${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`; })()}
              </span>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn-icon" onClick={printAttachment} title="ბეჭდვა"><FiPrinter /></button>
                <button className="btn-icon" onClick={() => setShowView(false)}><FiX /></button>
              </div>
            </div>
            <div className="paper">
              <div className="paper-title-right">{viewData.template_title}</div>
              {viewData.template_main_title && <div className="paper-center"><div className="paper-main-title">{viewData.template_main_title}</div></div>}
              {viewData.template_subtitle && <div className="paper-center"><div className="paper-subtitle">{viewData.template_subtitle}</div></div>}
              <div className="paper-fields">
                {(viewData.template_header_fields || []).map((f, i) => (
                  <div key={i} className="paper-field">
                    <span className="paper-field-label">{f.label}</span>
                    <span className="paper-field-value">
                      {f.type === 'client_dropdown' ? `${viewData.client_first_name || ''} ${viewData.client_last_name || ''} - ${viewData.client_pn || ''}` :
                       f.type === 'client_address' ? ((viewData.header_values || {})[i] || '') :
                       f.type === 'client_phone' ? ((viewData.header_values || {})[i] || '') :
                       f.type === 'auto_user' ? `${viewData.user_first_name || ''} ${viewData.user_last_name || ''} - ${viewData.user_pn || ''}` :
                       f.type === 'auto_user_phone' ? ((viewData.header_values || {})[i] || '') :
                       (viewData.header_values || {})[i] || ''}
                    </span>
                  </div>
                ))}
              </div>
              {(() => {
                const cols = (viewData.template_columns || []).map((c, i) => ({ ...c, idx: i }));
                const rd = viewData.row_data || {};

                // Detect weekly: days contain plain objects (not arrays) for numeric keys
                const isWeekly = rd.type === 'weekly' || (rd.days && rd.weekStart && (() => {
                  const numKeys = Object.keys(rd.days).filter(k => !isNaN(parseInt(k)));
                  return numKeys.length > 0 && numKeys.some(k => rd.days[k] && !Array.isArray(rd.days[k]) && typeof rd.days[k] === 'object');
                })());

                // Calendar view
                if (!isWeekly && (rd.type === 'calendar' || rd.days)) {
                  const days = rd.days || {};
                  const weekStart = rd.weekStart;
                  const dayNames = ['ორშაბათი','სამშაბათი','ოთხშაბათი','ხუთშაბათი','პარასკევი','შაბათი','კვირა'];
                  return (
                    <div className="paper-calendar-view">
                      {weekStart && <div className="paper-week-label">კვირა: {weekStart}</div>}
                      {Object.keys(days).sort((a, b) => a - b).map((dayIdx) => {
                        const entries = days[dayIdx] || [];
                        const monday = weekStart ? new Date(weekStart) : null;
                        let dateStr = '';
                        if (monday) {
                          const d = new Date(monday);
                          d.setDate(d.getDate() + parseInt(dayIdx));
                          dateStr = d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
                        }
                        return (
                          <div key={dayIdx} className="paper-cal-day">
                            <div className="paper-cal-day-header">
                              <strong>{dayNames[dayIdx]}</strong>
                              {dateStr && <span>{dateStr}</span>}
                            </div>
                            {entries.length > 0 && entries.some(e => Object.values(e).some(v => v)) ? (
                              <table className="paper-table" style={{marginBottom: 0}}>
                                <thead><tr><th className="paper-col-num">№</th>
                                  {cols.map((c) => <th key={c.idx} style={c.width ? {width: `${c.width}%`} : undefined}>{c.name}</th>)}
                                </tr></thead>
                                <tbody>
                                  {entries.filter(e => Object.values(e).some(v => v)).map((entry, ei) => (
                                    <tr key={ei}>
                                      <td className="paper-col-num">{ei + 1}</td>
                                      {cols.map((c) => <td key={c.idx}>{entry[c.idx] || ''}</td>)}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            ) : (
                              <div style={{padding: '8px 12px', color: '#94a3b8', fontSize: 13}}>ჩანაწერები არ არის</div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                }

                // Weekly plan view
                if (isWeekly) {
                  const days = rd.days || {};
                  const weekStart = rd.weekStart;
                  const visibleCols = cols.filter(c => c.type !== 'datetime' && c.type !== 'duration');
                  const dayKeys = Object.keys(days).filter(k => k !== '_fullRow' && k !== '_skipDays' && !isNaN(parseInt(k))).sort((a, b) => a - b);
                  const dayNames = ['ორშაბათი','სამშაბათი','ოთხშაბათი','ხუთშაბათი','პარასკევი','შაბათი','კვირა'];
                  // Format week range
                  let weekLabel = weekStart || '';
                  if (weekStart) {
                    const ws = new Date(weekStart);
                    const we = new Date(ws);
                    we.setDate(we.getDate() + 6);
                    weekLabel = `${ws.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })} — ${we.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}`;
                  }

                  return (
                    <div className="paper-table-wrap">
                      {weekStart && <div className="paper-week-label" style={{marginBottom: 12}}>კვირა: {weekLabel}</div>}
                      <table className="paper-table">
                        <thead><tr>
                          <th>დღე</th>
                          <th>თარიღი</th>
                          {visibleCols.map((c) => <th key={c.idx} className={c.fullRow ? 'paper-col-fullrow' : ''} style={c.width ? {width: `${c.width}%`} : undefined}>{c.name}</th>)}
                        </tr></thead>
                        <tbody>
                          {dayKeys.map((dayIdx, di) => {
                            const raw = days[dayIdx] || {};
                            const entries = Array.isArray(raw) ? raw : [raw];
                            const isSkipped = entries[0]?._skipped || raw._skipped;
                            const monday = weekStart ? new Date(weekStart) : null;
                            let dateStr = '';
                            if (monday) {
                              const d = new Date(monday);
                              d.setDate(d.getDate() + parseInt(dayIdx));
                              dateStr = d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
                            }
                            const totalRows = dayKeys.reduce((sum, k) => {
                              const r = days[k]; return sum + (Array.isArray(r) ? r.length : 1);
                            }, 0);
                            return entries.map((entryData, ei) => (
                              <tr key={`${dayIdx}-${ei}`} style={isSkipped ? {opacity: 0.4} : {}}>
                                {ei === 0 && <td style={{ fontWeight: 600, whiteSpace: 'nowrap' }} rowSpan={entries.length}>{dayNames[dayIdx] || `დღე ${parseInt(dayIdx) + 1}`}</td>}
                                {ei === 0 && <td style={{ whiteSpace: 'nowrap' }} rowSpan={entries.length}>{dateStr}</td>}
                                {visibleCols.map((c) => {
                                  if (c.fullRow && di === 0 && ei === 0) {
                                    const fullRowObj = days._fullRow || {};
                                    const val = fullRowObj[c.idx] || fullRowObj[String(c.idx)] || '';
                                    return (
                                      <td key={c.idx} rowSpan={totalRows} className="paper-cell-fullrow" style={{ verticalAlign: 'top', fontSize: 13, whiteSpace: 'pre-wrap' }}>
                                        {c.type === 'signature' && val ? <img src={val} alt="" style={{ height: 40, objectFit: 'contain' }} /> : val}
                                      </td>
                                    );
                                  }
                                  if (c.fullRow && (di > 0 || ei > 0)) return null;
                                  if (c.fullRow) return null;
                                  const cellVal = entryData[c.idx] || '';
                                  const strVal = typeof cellVal === 'string' ? cellVal : String(cellVal || '');
                                  return (
                                    <td key={c.idx} style={{ whiteSpace: 'pre-wrap', textAlign: 'left' }}>
                                      {isSkipped ? '—' : c.type === 'signature' && cellVal ? <img src={cellVal} alt="" style={{ height: 25, objectFit: 'contain' }} /> : c.type === 'worktime' && strVal ? (
                                        <span className="tp-view">{strVal.split(' - ').map((t, ti) => <span key={ti} className="tp-view-time">{t}</span>).reduce((a, b) => [a, <span key="arrow" className="tp-view-arrow">→</span>, b])}</span>
                                      ) : c.type === 'activity' && strVal && (strVal.includes(' || ') || strVal.includes(', ')) ? (
                                        parseActivities(strVal, activities.map(a => a.name)).map((act, ai) => <div key={ai} style={{ padding: '1px 0' }}>• {act}</div>)
                                      ) : strVal}
                                    </td>
                                  );
                                })}
                              </tr>
                            ));
                          })}
                        </tbody>
                      </table>
                    </div>
                  );
                }

                // Standard view
                const rows = rd.rows || (Array.isArray(rd) ? rd : []);
                const rawFullRows = rd.fullRows || {};
                // Normalize keys to numbers for consistent access
                const fullRows = {};
                Object.keys(rawFullRows).forEach(k => { fullRows[Number(k)] = rawFullRows[k]; });
                const rowCount = Math.max(rows.length, 1);

                return (
                  <div className="paper-table-wrap">
                    <table className="paper-table">
                      <thead><tr><th className="paper-col-num">№</th>
                        {cols.map((c) => (<th key={c.idx} className={c.fullRow ? 'paper-col-fullrow' : ''} style={c.width ? {width: `${c.width}%`} : undefined}>{c.name}</th>))}
                      </tr></thead>
                      <tbody>
                        {rows.map((row, ri) => (
                          <tr key={ri}>
                            <td className="paper-col-num">{ri + 1}</td>
                            {cols.map((c) => {
                              if (c.fullRow && ri === 0) {
                                const items = fullRows[c.idx] || [];
                                return (
                                  <td key={c.idx} rowSpan={rowCount} className="paper-cell-fullrow" style={{ verticalAlign: 'middle', fontSize: 13 }}>
                                    {items.map((val, vi) => (
                                      <div key={vi} style={{ padding: '2px 0' }}>
                                        {c.type === 'signature' && val ? <img src={val} alt="" style={{ height: 25, objectFit: 'contain' }} /> : c.type === 'activity' && val && (val.includes(' || ') || val.includes(', ')) ? (
                                          parseActivities(val, activities.map(a => a.name)).map((act, ai) => <div key={ai} style={{ padding: '1px 0' }}>• {act}</div>)
                                        ) : (val || '')}
                                      </div>
                                    ))}
                                  </td>
                                );
                              }
                              if (c.fullRow) return null;
                              return (
                                <td key={c.idx}>
                                  {c.type === 'signature' && row[c.idx] ? <img src={row[c.idx]} alt="" style={{ height: 25, objectFit: 'contain' }} /> : (row[c.idx] || '')}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              })()}
              {viewData.note && <div className="paper-note"><strong>შენიშვნა:</strong> {viewData.note}</div>}
              {false && viewData.duration_seconds && (
                <div className="paper-duration">ხანგრძლივობა: {formatTime(viewData.duration_seconds)}</div>
              )}
            </div>
          </div>
        </div>
      )}

      {confirmAction && <ConfirmModal {...confirmAction} onCancel={() => setConfirmAction(null)} />}
    </div>
  );
};

export default UserDashboard;
